import React, { useMemo, useState } from 'react';
import { buildConflictNotes } from '../../utils/eliminatorConflictNotes';
import {
	coordsToOverallPick,
	DRAFT_ROUNDS,
	DRAFT_SLOT_COUNT,
	DRAFT_TEAMS,
	formatDraftPickLabel,
	formatShortPlayerName,
	getTeamAbbreviation,
	isRoundLeftToRight,
	isUndraftedSlot,
	UNDRAFTED_COUNT
} from '../../utils/eliminatorDraftBoard';
import EliminatorConflictNotes from './EliminatorConflictNotes';

const POSITION_CARD_CLASS = {
	QB: 'border-purple-500 bg-purple-900 bg-opacity-30',
	RB: 'border-green-500 bg-green-900 bg-opacity-30',
	WR: 'border-yellow-500 bg-yellow-900 bg-opacity-30',
	TE: 'border-blue-500 bg-blue-900 bg-opacity-30'
};

const BOARD_GRID_COLUMNS = `2.5rem repeat(${DRAFT_TEAMS}, minmax(6rem, 1fr))`;
const UNDRAFTED_ROW_COUNT = Math.ceil(UNDRAFTED_COUNT / DRAFT_TEAMS);

function DraftPlayerCard({
	entry,
	overallPick,
	teamLookup,
	isDragging,
	isDropTarget,
	onDragStart,
	onDragOver,
	onDrop,
	onDragEnd
}) {
	const abbr = getTeamAbbreviation(entry, teamLookup);
	const position = entry?.position || '';
	const undrafted = isUndraftedSlot(overallPick);
	const cardClass = entry
		? POSITION_CARD_CLASS[position] || 'border-gray-600 bg-gray-800'
		: 'border-dashed border-gray-600 bg-gray-900 bg-opacity-40';

	return (
		<div
			draggable={Boolean(entry)}
			onDragStart={onDragStart}
			onDragOver={onDragOver}
			onDrop={onDrop}
			onDragEnd={onDragEnd}
			className={`relative h-14 w-full rounded border px-1.5 py-1 pr-5 ${cardClass} ${
				isDragging ? 'opacity-40' : ''
			} ${isDropTarget ? 'border-2 border-blue-400' : ''} ${
				entry ? 'cursor-move' : 'cursor-default'
			}`}
		>
			<span className="absolute top-0.5 right-1 text-2xs font-semibold text-gray-400">
				{overallPick + 1}
			</span>
			{entry ? (
				<>
					<div className="truncate text-2xs font-semibold text-white">
						{formatShortPlayerName(entry)}
					</div>
					<div className="truncate text-2xs text-gray-300">
						{position}
						{abbr ? ` · ${abbr}` : ''}
					</div>
					<div className="mt-0.5 text-2xs text-gray-500">
						{formatDraftPickLabel(overallPick)}
					</div>
				</>
			) : (
				<div className="flex h-full items-center justify-center text-2xs text-gray-500">
					{undrafted ? 'UD' : '—'}
				</div>
			)}
		</div>
	);
}

function SlotCard({
	overallPick,
	slots,
	entriesById,
	teamLookup,
	dragIndex,
	dropIndex,
	setDragIndex,
	setDropIndex,
	onSwap
}) {
	const entryId = slots[overallPick];
	const entry = entryId != null ? entriesById[entryId] : null;

	return (
		<DraftPlayerCard
			entry={entry}
			overallPick={overallPick}
			teamLookup={teamLookup}
			isDragging={dragIndex === overallPick}
			isDropTarget={dropIndex === overallPick}
			onDragStart={(event) => {
				if (!entry) {
					event.preventDefault();
					return;
				}
				event.dataTransfer.effectAllowed = 'move';
				event.dataTransfer.setData('text/plain', String(overallPick));
				setDragIndex(overallPick);
			}}
			onDragOver={(event) => {
				event.preventDefault();
				event.dataTransfer.dropEffect = 'move';
				if (dropIndex !== overallPick) {
					setDropIndex(overallPick);
				}
			}}
			onDrop={(event) => {
				event.preventDefault();
				const fromRaw = event.dataTransfer.getData('text/plain');
				const fromIndex = Number(fromRaw);
				if (Number.isFinite(fromIndex)) {
					onSwap(fromIndex, overallPick);
				}
				setDragIndex(null);
				setDropIndex(null);
			}}
			onDragEnd={() => {
				setDragIndex(null);
				setDropIndex(null);
			}}
		/>
	);
}

export default function EliminatorDraftBoardRail({
	isOpen,
	onClose,
	onSwitchToRoster,
	slots,
	entriesById,
	weeks,
	teamLookup,
	onSwap,
	onReset,
	onCreateRosterFromTeam
}) {
	const [selectedTeam, setSelectedTeam] = useState(0);
	const [dragIndex, setDragIndex] = useState(null);
	const [dropIndex, setDropIndex] = useState(null);

	const selectedTeamPlayers = useMemo(() => {
		const players = [];
		for (let round = 0; round < DRAFT_ROUNDS; round += 1) {
			const overallPick = coordsToOverallPick(selectedTeam, round);
			const entryId = slots[overallPick];
			if (entryId != null && entriesById[entryId]) {
				players.push(entriesById[entryId]);
			}
		}
		return players;
	}, [selectedTeam, slots, entriesById]);

	const conflictNotes = useMemo(
		() => buildConflictNotes(selectedTeamPlayers, weeks, teamLookup),
		[selectedTeamPlayers, weeks, teamLookup]
	);

	const undraftedFilledCount = useMemo(() => {
		let count = 0;
		for (let index = DRAFT_SLOT_COUNT; index < slots.length; index += 1) {
			if (slots[index] != null) count += 1;
		}
		return count;
	}, [slots]);

	const handleCreateRosterClick = (teamIndex, event) => {
		event.stopPropagation();
		const confirmed = window.confirm(
			`Do you want to create a roster based on Team ${teamIndex + 1}? This clears your current roster and replaces it with the players in this column.`
		);
		if (!confirmed) return;
		setSelectedTeam(teamIndex);
		onCreateRosterFromTeam(teamIndex);
	};

	if (!isOpen) return null;

	return (
		<aside
			className="fixed right-0 top-0 z-50 flex h-full w-full max-w-full flex-col border-l border-gray-300 bg-ff-black shadow-xl dark:border-dark-border"
			aria-label="Eliminator draft board"
		>
			<div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-700 bg-gray-900 px-4 py-3">
				<div>
					<h2 className="text-base font-semibold text-white">Draft Board</h2>
					<p className="text-xs text-gray-400">
						{DRAFT_ROUNDS} rounds · {DRAFT_TEAMS} teams · {UNDRAFTED_COUNT} undrafted ·
						drag to swap (including from Undrafted) · + builds roster from a team
					</p>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<button
						type="button"
						onClick={onSwitchToRoster}
						className="rounded-md border border-gray-600 px-2.5 py-1.5 text-xs font-medium text-gray-200 hover:bg-gray-800"
					>
						Roster
					</button>
					<button
						type="button"
						onClick={onReset}
						className="rounded-md border border-gray-600 px-2.5 py-1.5 text-xs font-medium text-gray-200 hover:bg-gray-800"
					>
						Reset snake
					</button>
					<button
						type="button"
						onClick={onClose}
						className="rounded-md bg-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-900 hover:bg-white"
					>
						Close
					</button>
				</div>
			</div>

			<div className="flex-1 overflow-auto px-3 py-3">
				<div className="w-full">
					<div
						className="mb-2 grid gap-1"
						style={{ gridTemplateColumns: BOARD_GRID_COLUMNS }}
					>
						<div />
						{Array.from({ length: DRAFT_TEAMS }, (_, teamIndex) => (
							<div
								key={`team-head-${teamIndex}`}
								className={`flex items-center justify-center gap-1 rounded px-1 py-1 ${
									selectedTeam === teamIndex ? 'bg-blue-900 bg-opacity-50' : ''
								}`}
							>
								<button
									type="button"
									onClick={() => setSelectedTeam(teamIndex)}
									className={`text-2xs font-semibold ${
										selectedTeam === teamIndex
											? 'text-white'
											: 'text-gray-300 hover:text-white'
									}`}
									title={`Check Team ${teamIndex + 1} stack`}
								>
									T{teamIndex + 1}
								</button>
								<button
									type="button"
									onClick={(event) => handleCreateRosterClick(teamIndex, event)}
									className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-gray-500 bg-gray-800 text-sm font-semibold leading-none text-gray-100 hover:bg-gray-700"
									title={`Create roster from Team ${teamIndex + 1}`}
									aria-label={`Create roster from Team ${teamIndex + 1}`}
								>
									+
								</button>
							</div>
						))}
					</div>

					<div className="space-y-1">
						{Array.from({ length: DRAFT_ROUNDS }, (_, round) => (
							<div
								key={`round-${round}`}
								className="grid items-stretch gap-1"
								style={{ gridTemplateColumns: BOARD_GRID_COLUMNS }}
							>
								<div className="flex flex-col items-center justify-center text-2xs font-semibold text-gray-400">
									<span>{round + 1}</span>
									<span aria-hidden="true">
										{isRoundLeftToRight(round) ? '→' : '←'}
									</span>
								</div>
								{Array.from({ length: DRAFT_TEAMS }, (_, teamIndex) => (
									<SlotCard
										key={`slot-${coordsToOverallPick(teamIndex, round)}`}
										overallPick={coordsToOverallPick(teamIndex, round)}
										slots={slots}
										entriesById={entriesById}
										teamLookup={teamLookup}
										dragIndex={dragIndex}
										dropIndex={dropIndex}
										setDragIndex={setDragIndex}
										setDropIndex={setDropIndex}
										onSwap={onSwap}
									/>
								))}
							</div>
						))}
					</div>

					<div className="mt-4 mb-2 flex items-center gap-2 border-t border-gray-700 pt-4">
						<span className="text-xs font-bold uppercase tracking-wide text-gray-300">
							Undrafted
						</span>
						<span className="text-2xs text-gray-500">
							{undraftedFilledCount} players · drag onto a drafted slot to pull up
						</span>
					</div>

					<div className="space-y-1">
						{Array.from({ length: UNDRAFTED_ROW_COUNT }, (_, rowIndex) => (
							<div
								key={`undrafted-row-${rowIndex}`}
								className="grid items-stretch gap-1"
								style={{ gridTemplateColumns: BOARD_GRID_COLUMNS }}
							>
								<div className="flex flex-col items-center justify-center text-2xs font-semibold text-gray-500">
									<span>UD</span>
								</div>
								{Array.from({ length: DRAFT_TEAMS }, (_, colIndex) => {
									const overallPick =
										DRAFT_SLOT_COUNT + rowIndex * DRAFT_TEAMS + colIndex;
									if (overallPick >= DRAFT_SLOT_COUNT + UNDRAFTED_COUNT) {
										return <div key={`ud-empty-${colIndex}`} />;
									}

									return (
										<SlotCard
											key={`slot-${overallPick}`}
											overallPick={overallPick}
											slots={slots}
											entriesById={entriesById}
											teamLookup={teamLookup}
											dragIndex={dragIndex}
											dropIndex={dropIndex}
											setDragIndex={setDragIndex}
											setDropIndex={setDropIndex}
											onSwap={onSwap}
										/>
									);
								})}
							</div>
						))}
					</div>
				</div>

				<div className="mt-4 rounded-md border border-gray-700 bg-gray-900 px-4 py-3">
					<h3 className="text-xs font-bold uppercase tracking-wide text-gray-400">
						Team {selectedTeam + 1} stack check
					</h3>
					<p className="mt-1 text-2xs text-gray-500">
						Favorable weeks should stagger across your roster; shared byes and stacked
						unfavorable weeks are called out below.
					</p>
					<EliminatorConflictNotes notes={conflictNotes} />
				</div>
			</div>
		</aside>
	);
}
